import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import CrudListPage from "../../../components/crud/CrudListPage";
import DataTable, { formatCurrency } from "../../../components/crud/DataTable";
import DetailPanel, { PanelSection, PanelRow, SummaryCard, SummaryRow } from "../../../components/v4/DetailPanel";
import { FiTrendingUp, FiEdit2 } from "react-icons/fi";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

const BudgetItemList = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState("all"); // all, expenses, income
	const [selectedItem, setSelectedItem] = useState(null);
	const [activeTab, setActiveTab] = useState("overview");

	useEffect(() => {
		const fetchItems = async () => {
			try {
				const res = await axios.get(`${backendUrl}/budget/items`);
				setData(res.data);
			} catch (err) {
				console.error("Error fetching budget items:", err);
			} finally {
				setLoading(false);
			}
		};
		fetchItems();
	}, []);

	const handleDelete = async (row) => {
		if (!window.confirm(`Delete "${row.name}"?`)) return;

		try {
			await axios.delete(`${backendUrl}/budget/items/${row.id}`);
			setData((prev) => prev.filter((item) => item.id !== row.id));
			if (selectedItem?.id === row.id) {
				setSelectedItem(null);
			}
		} catch (err) {
			console.error("Error deleting budget item:", err);
		}
	};

	const handleRowClick = (row) => {
		setSelectedItem(row);
		setActiveTab("overview");
	};

	// Panel tabs
	const panelTabs = [
		{ id: "overview", label: "Overview" },
		{ id: "schedule", label: "Schedule" },
	];

	// Render panel content
	const renderPanelContent = () => {
		if (!selectedItem) return null;

		if (activeTab === "overview") {
			return (
				<>
					<PanelSection title="Budget Item Details">
						<PanelRow label="Name" value={selectedItem.name} />
						<PanelRow label="Section" value={selectedItem.section || "—"} />
						<PanelRow label="Category" value={selectedItem.category || "—"} />
						<PanelRow
							label="Amount"
							value={`${selectedItem.is_income ? "+" : "-"}${formatCurrency(Math.abs(selectedItem.amount))}`}
						/>
						<PanelRow
							label="Type"
							value={selectedItem.is_income ? "Income" : "Expense"}
						/>
					</PanelSection>
				</>
			);
		}

		if (activeTab === "schedule") {
			return (
				<PanelSection title="Recurrence">
					<PanelRow label="Schedule" value={formatRecurrence(selectedItem)} />
					{selectedItem.start_date && (
						<PanelRow
							label="Start Date"
							value={new Date(selectedItem.start_date).toLocaleDateString()}
						/>
					)}
					{selectedItem.end_date && (
						<PanelRow
							label="End Date"
							value={new Date(selectedItem.end_date).toLocaleDateString()}
						/>
					)}
				</PanelSection>
			);
		}

		return null;
	};

	// Render panel sidebar
	const renderPanelSidebar = () => {
		if (!selectedItem) return null;

		return (
			<SummaryCard label="Quick Info">
				<SummaryRow
					label="Amount"
					value={`${selectedItem.is_income ? "+" : "-"}${formatCurrency(Math.abs(selectedItem.amount))}`}
				/>
				<SummaryRow
					label="Type"
					value={selectedItem.is_income ? "Income" : "Expense"}
				/>
			</SummaryCard>
		);
	};

	// Panel footer
	const renderPanelFooter = () => {
		if (!selectedItem) return null;
		return (
			<Link to={`/budget-items/${selectedItem.id}`} className="btn btn--primary">
				<FiEdit2 style={{ marginRight: 6 }} />
				Edit
			</Link>
		);
	};

	// Filter data
	const filteredData = data.filter((item) => {
		if (filter === "expenses") return !item.is_income;
		if (filter === "income") return item.is_income;
		return true;
	});

	// Format recurrence for display
	const formatRecurrence = (item) => {
		const parts = [];

		if (item.specific_months) {
			const months = typeof item.specific_months === "string"
				? JSON.parse(item.specific_months)
				: item.specific_months;
			if (months && months.length > 0) {
				const monthNames = months.map((m) =>
					new Date(2000, m - 1).toLocaleString("default", { month: "short" })
				);
				parts.push(monthNames.join(", "));
			}
		}

		if (item.start_date || item.end_date) {
			const start = item.start_date ? new Date(item.start_date).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "∞";
			const end = item.end_date ? new Date(item.end_date).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "∞";
			parts.push(`${start} → ${end}`);
		}

		return parts.length > 0 ? parts.join(" | ") : "Every month";
	};

	const columns = [
		{
			key: "name",
			header: "Name",
			width: "lg",
			render: (name, row) => (
				<Link to={`/budget-items/${row.id}`} style={{ fontWeight: 600 }}>
					{name}
				</Link>
			),
		},
		{
			key: "section",
			header: "Section",
			width: "sm",
		},
		{
			key: "category",
			header: "Category",
			width: "sm",
			render: (cat) => cat || "—",
		},
		{
			key: "amount",
			header: "Amount",
			width: "sm",
			render: (amount, row) => {
				const formatted = formatCurrency(Math.abs(amount));
				return (
					<span style={{ color: row.is_income ? "var(--v4-success)" : "var(--v4-text-secondary)" }}>
						{row.is_income ? "+" : "-"}{formatted}
					</span>
				);
			},
		},
		{
			key: "recurrence",
			header: "Recurrence",
			render: (_, row) => formatRecurrence(row),
		},
	];

	// Filter buttons
	const filterButtons = (
		<div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
			<button
				onClick={() => setFilter("all")}
				className={`btn ${filter === "all" ? "btn--primary" : "btn--secondary"}`}
			>
				All ({data.length})
			</button>
			<button
				onClick={() => setFilter("expenses")}
				className={`btn ${filter === "expenses" ? "btn--primary" : "btn--secondary"}`}
			>
				Expenses ({data.filter((d) => !d.is_income).length})
			</button>
			<button
				onClick={() => setFilter("income")}
				className={`btn ${filter === "income" ? "btn--primary" : "btn--secondary"}`}
			>
				Income ({data.filter((d) => d.is_income).length})
			</button>
		</div>
	);

	return (
		<>
			<CrudListPage
				title="Budget Items"
				addLink="/budget-items/new"
				addLabel="Add Item"
				variant="v4"
				icon={<FiTrendingUp />}
			>
				{filterButtons}
				<DataTable
					columns={columns}
					data={filteredData}
					onDelete={handleDelete}
					loading={loading}
					emptyMessage="No budget items found. Add your first item above."
					variant="v4"
					onRowClick={handleRowClick}
					selectedId={selectedItem?.id}
				/>
			</CrudListPage>

			<DetailPanel
				isOpen={!!selectedItem}
				onClose={() => setSelectedItem(null)}
				title={selectedItem?.name || ""}
				subtitle="Budget Item"
				tabs={panelTabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				sidebar={renderPanelSidebar()}
				footer={renderPanelFooter()}
			>
				{renderPanelContent()}
			</DetailPanel>
		</>
	);
};

export default BudgetItemList;
